import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyDashboard } from '../../api/company/client'
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  Percent,
  ArrowUpRight,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { Skeleton } from '../../components/company/Skeleton'

const STATUS_KEYS: Record<string, string> = {
  completed: 'company.statusCompleted',
  pending: 'company.statusPending',
  failed: 'company.statusFailed',
  refunded: 'company.statusRefunded',
}

export default function CompanyOverview() {
  const { t } = useTranslation('portal')
  const { data, isLoading, error } = useQuery({
    queryKey: ['company', 'dashboard'],
    queryFn: () => companyDashboard.overview().then((r) => r.data),
  })

  if (isLoading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.overview.title')}</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.overview.title')}</h1>
        <p className="text-red-600">{t('company.overview.failedToLoad')}</p>
      </div>
    )
  }

  const cards = [
    { labelKey: 'company.overview.transactions', value: data?.total_transactions ?? 0, icon: CreditCard },
    { labelKey: 'company.overview.totalRevenueBam', value: Number(data?.total_revenue ?? 0).toFixed(2), icon: DollarSign },
    { labelKey: 'company.overview.totalFeesBam', value: Number(data?.total_fees ?? 0).toFixed(2), icon: Percent },
    { labelKey: 'company.overview.netAmountBam', value: Number(data?.net_amount ?? 0).toFixed(2), icon: TrendingUp },
  ]

  const chartData =
    data?.daily_trend?.map((d) => ({
      date: d.date,
      volume: d.volume,
      count: d.transactions,
    })) ?? []

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.overview.title')}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(({ labelKey, value, icon: Icon }) => (
          <div
            key={labelKey}
            className="rounded-xl bg-white border border-gray-200 p-4 flex items-start gap-3 shadow-sm"
          >
            <div className="rounded-lg bg-blue-50 p-2">
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-gray-500 text-sm">{t(labelKey)}</p>
              <p className="text-xl font-semibold text-gray-900 truncate">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 rounded-xl bg-white border border-gray-200 p-4 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.overview.revenueTrend')}</h2>
          {chartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    tickFormatter={(v) => format(new Date(v), 'MMM d')}
                  />
                  <YAxis
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    tickFormatter={(v) => `${v} BAM`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                    labelFormatter={(v) => format(new Date(v), 'PPP')}
                    formatter={(value: number) => [`${Number(value).toFixed(2)} BAM`, t('company.overview.volume')]}
                  />
                  <Area
                    type="monotone"
                    dataKey="volume"
                    stroke="#2563eb"
                    fill="#2563eb"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-8">{t('company.overview.noDataPeriod')}</p>
          )}
        </div>

        <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('company.overview.upcomingPayout')}</h2>
          {data?.next_payout_date ? (
            <>
              <p className="text-gray-500 text-sm">{t('company.overview.nextPayoutDate')}</p>
              <p className="text-gray-900 font-semibold text-lg">
                {format(new Date(data.next_payout_date), 'PPP')}
              </p>
              {data.estimated_payout_amount != null && (
                <>
                  <p className="text-gray-500 text-sm mt-3">{t('company.overview.estimatedAmount')}</p>
                  <p className="text-blue-600 font-semibold text-lg">
                    {Number(data.estimated_payout_amount).toFixed(2)} BAM
                  </p>
                </>
              )}
              {data.days_until_payout != null && data.days_until_payout >= 0 && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{t('company.overview.daysUntilPayout')}</span>
                    <span>{t('company.overview.daysCount', { count: data.days_until_payout })}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, 100 - (data.days_until_payout ?? 0) * 3)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm">{t('company.overview.noUpcomingPayout')}</p>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">{t('company.overview.recentTransactions')}</h2>
          <Link
            to="/company/transactions"
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {t('company.overview.viewAll')} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[560px]">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 text-sm">
                <th className="p-3">{t('company.overview.fiscalNumber')}</th>
                <th className="p-3">{t('company.overview.amount')}</th>
                <th className="p-3">{t('company.overview.status')}</th>
                <th className="p-3">{t('company.overview.date')}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_transactions ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-6 text-gray-500 text-center">
                    {t('company.overview.noRecentTransactions')}
                  </td>
                </tr>
              ) : (
                (data?.recent_transactions ?? []).map((tx) => (
                  <tr key={tx.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3">
                      <span className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/company/transactions/${tx.id}`}
                          className="text-blue-600 hover:underline font-mono text-sm"
                        >
                          {tx.fiscal_number}
                        </Link>
                        {tx.is_internal_receipt && (
                          <span className="rounded px-2 py-0.5 text-xs bg-violet-100 text-violet-700 whitespace-nowrap">
                            {t('company.overview.internalReceipt')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-gray-900">
                      {Number(tx.amount).toFixed(2)} {tx.currency}
                    </td>
                    <td className="p-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${
                          tx.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : tx.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {STATUS_KEYS[tx.status] ? t(STATUS_KEYS[tx.status]) : tx.status}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-sm">
                      {tx.created_at ? format(new Date(tx.created_at), 'PPp') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
